import { Controller } from "@hotwired/stimulus"

/**
 * Modal dialog controller with focus trapping and accessibility
 *
 * Usage:
 * <div data-controller="modal" data-modal-open-value="false">
 *   <button data-action="click->modal#open">Open Modal</button>
 *
 *   <div data-modal-target="backdrop" class="hidden fixed inset-0 bg-black/50 z-40" data-action="click->modal#closeOnBackdrop"></div>
 *   <div data-modal-target="dialog" class="hidden fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="modal-title">
 *     <div class="bg-white rounded-2xl p-6 max-w-lg w-full" data-modal-target="content">
 *       <h2 id="modal-title">Dialog Title</h2>
 *       <button data-action="click->modal#close" aria-label="Close">X</button>
 *       <!-- Modal content -->
 *     </div>
 *   </div>
 * </div>
 *
 * Accessibility notes:
 * - Always include aria-labelledby pointing to the modal title
 * - Focus is automatically trapped within the modal
 * - Escape key closes the modal
 * - Focus returns to trigger element on close
 */
export default class extends Controller {
  static targets = ["backdrop", "dialog", "content"]
  static values = {
    open: { type: Boolean, default: false }
  }

  connect() {
    this.boundKeydown = this.keydown.bind(this)
    this.previousActiveElement = null
    this.focusableElements = []
  }

  disconnect() {
    document.removeEventListener("keydown", this.boundKeydown)
    this.restoreBodyScroll()
  }

  open(event) {
    if (event) event.preventDefault()

    this.previousActiveElement = document.activeElement
    this.openValue = true

    if (this.hasBackdropTarget) {
      this.backdropTarget.classList.remove("hidden")
    }

    if (this.hasDialogTarget) {
      this.dialogTarget.classList.remove("hidden")
    }

    this.lockBodyScroll()
    document.addEventListener("keydown", this.boundKeydown)

    // Focus the first focusable element in the modal
    requestAnimationFrame(() => {
      this.trapFocus()
    })

    this.dispatch("opened")
  }

  close(event) {
    if (event) event.preventDefault()

    this.openValue = false

    if (this.hasBackdropTarget) {
      this.backdropTarget.classList.add("hidden")
    }

    if (this.hasDialogTarget) {
      this.dialogTarget.classList.add("hidden")
    }

    this.restoreBodyScroll()
    document.removeEventListener("keydown", this.boundKeydown)

    // Return focus to the element that opened the modal
    if (this.previousActiveElement) {
      this.previousActiveElement.focus()
    }

    this.dispatch("closed")
  }

  closeOnBackdrop(event) {
    // Only close if clicking directly on the backdrop
    if (event.target === this.backdropTarget || event.target === this.dialogTarget) {
      this.close(event)
    }
  }

  keydown(event) {
    if (event.key === "Escape") {
      this.close(event)
      return
    }

    if (event.key === "Tab") {
      this.handleTabKey(event)
    }
  }

  trapFocus() {
    if (!this.hasContentTarget) return

    const focusableSelectors = [
      "a[href]",
      "button:not([disabled])",
      "input:not([disabled])",
      "select:not([disabled])",
      "textarea:not([disabled])",
      "[tabindex]:not([tabindex='-1'])"
    ].join(", ")

    this.focusableElements = Array.from(
      this.contentTarget.querySelectorAll(focusableSelectors)
    ).filter(el => el.offsetParent !== null)

    if (this.focusableElements.length > 0) {
      this.focusableElements[0].focus()
    }
  }

  handleTabKey(event) {
    if (this.focusableElements.length === 0) return

    const firstElement = this.focusableElements[0]
    const lastElement = this.focusableElements[this.focusableElements.length - 1]

    if (event.shiftKey) {
      // Shift + Tab
      if (document.activeElement === firstElement) {
        event.preventDefault()
        lastElement.focus()
      }
    } else {
      // Tab
      if (document.activeElement === lastElement) {
        event.preventDefault()
        firstElement.focus()
      }
    }
  }

  lockBodyScroll() {
    document.body.style.overflow = "hidden"
  }

  restoreBodyScroll() {
    document.body.style.overflow = ""
  }

  openValueChanged() {
    // Hook for any additional logic when open state changes
  }
}
